/** Page-level lifted draft for the notification editor.
 *
 * Holds the whole editable aggregate (meta + delivery methods + block refs)
 * with a `dirtyPaths` set instead of deep-comparing 100KB template strings on
 * every render. Produces the sparse PATCH body (only dirty meta keys; the
 * COMPLETE delivery-method list with only dirty fields per row — the backend
 * reconciles by id and deletes missing rows, so the full list is mandatory
 * whenever any DM field or the order changed; likewise block_refs).
 *
 * `applySnapshot` loads a history snapshot into the draft (client-side
 * restore) marking every differing path dirty — saving then records a new,
 * fully attributed change row.
 */
import { useCallback, useMemo, useRef, useState } from 'react';

import type {
  AdvancedRuleRow,
  AdvancedRuleWrite,
  AutoReplies,
  AutoReplyOption,
  AutoReplyTagRef,
  BlockRefItem,
  BlockRefWrite,
  DeliveryMethodWrite,
  NotificationCore,
  NotificationDeliveryMethod,
  NotificationDetail,
  NotificationWrite,
} from '@/api/notifications';
import { RENDER_CUSTOM } from '@/api/notifications';
import type { NotificationSnapshot } from '@/api/notificationChanges';
import type { RuleWire } from '@/api/notificationRulesets';

import { allocClientRuleId, draftsFromDetail, rulesToWrite } from './rules/ruleSerialize';
import type { RuleDraft } from './rules/ruleTypes';

/** Editable meta keys the console edits directly (targeting stays read-only). */
const META_KEYS: (keyof NotificationCore)[] = [
  'name',
  'enabled',
  'language_policy',
  'disable_default_blocks',
];

export interface DraftState {
  meta: Partial<NotificationCore>;
  deliveryMethods: NotificationDeliveryMethod[];
  blockRefs: BlockRefItem[];
  rules: RuleDraft[];
  /** Negative ids = client rows (React keys only — ids are never sent). */
  advancedRules: AdvancedRuleRow[];
  autoReplies: AutoReplies | null;
}

export interface NotificationDraft {
  draft: DraftState;
  dirtyPaths: ReadonlySet<string>;
  dirty: boolean;
  setMeta: <K extends keyof NotificationCore>(key: K, value: NotificationCore[K]) => void;
  setDmField: (dmId: number, field: string, value: unknown) => void;
  /** Replace the whole ordered DM list (reorder / enable toggles included). */
  setDeliveryMethods: (dms: NotificationDeliveryMethod[], path?: string) => void;
  /** Append a NEW delivery method (client-side negative id; created on save).
   *  Returns the new row's id so the caller can select it. */
  addDeliveryMethod: (method: number) => number;
  /** Drop a delivery method row — the save's full-list reconcile DELETES it. */
  removeDeliveryMethod: (dmId: number) => void;
  setBlockRefs: (refs: BlockRefItem[]) => void;
  /** Replace the whole ordered rules list (add/edit/remove). */
  setRules: (rules: RuleDraft[]) => void;
  /** Replace the whole advanced-rules list (rows are recreated on save). */
  setAdvancedRules: (rows: AdvancedRuleRow[]) => void;
  /** Replace the auto-replies object (null = leave server state untouched
   *  until first edit; the section always sends the whole object). */
  setAutoReplies: (value: AutoReplies) => void;
  /** Load a snapshot (client-side restore); marks differing paths dirty. */
  applySnapshot: (snapshot: NonNullable<NotificationSnapshot>) => void;
  /** Sparse PATCH body from the dirty state. */
  buildPatch: (extra?: Pick<NotificationWrite, 'comment' | 'expected_updated_ts'>) => NotificationWrite;
  /** Reset the draft to a freshly fetched detail payload. */
  reset: (detail: NotificationDetail) => void;
}

/** Snapshot/read option tags are bare FK ids; the write funnel needs
 *  {id, …} objects (a bare int crashes it) — normalize on the way in. */
function normalizeAutoReplies(
  value: AutoReplies | null | undefined,
  tagRefs?: Record<string, AutoReplyTagRef>,
): AutoReplies | null {
  if (value == null) return null;
  return {
    ...value,
    options: (value.options ?? []).map((o) => ({
      ...o,
      tag:
        o.tag == null
          ? null
          : typeof o.tag === 'number'
            ? (tagRefs?.[String(o.tag)] ?? { id: o.tag })
            : o.tag,
    })),
  };
}

function initialState(detail: NotificationDetail): DraftState {
  return {
    meta: { ...detail.notification },
    deliveryMethods: detail.delivery_methods.map((dm) => ({ ...dm })),
    blockRefs: detail.block_refs.map((r) => ({ ...r })),
    rules: draftsFromDetail(detail.rules, detail.refs),
    advancedRules: detail.advanced_rules.map((r) => ({
      ...r,
      trigger_notification_delivery_methods: [...r.trigger_notification_delivery_methods],
      dependent_notification_delivery_methods: [...r.dependent_notification_delivery_methods],
    })),
    autoReplies: normalizeAutoReplies(detail.auto_replies, detail.refs.auto_reply_tags),
  };
}

/** Write rows are id-less — the backend recreates the whole set. */
function advancedRulesToWrite(rows: AdvancedRuleRow[]): AdvancedRuleWrite[] {
  return rows.map(({ id: _id, ...rest }) => ({
    ...rest,
    trigger_notification_delivery_methods: [...rest.trigger_notification_delivery_methods],
    dependent_notification_delivery_methods: [...rest.dependent_notification_delivery_methods],
  }));
}

function autoRepliesToWrite(value: AutoReplies): AutoReplies {
  return {
    ...value,
    options: value.options.map((o, index): AutoReplyOption => ({
      ...o,
      // Negative ids are client-side React keys — the wire contract is
      // id: null = create (a negative id would 400 the ownership check).
      id: o.id != null && o.id > 0 ? o.id : null,
      sequence: index,
      tag: o.tag == null ? null : typeof o.tag === 'number' ? { id: o.tag } : o.tag,
    })),
  };
}

/** A blank NDM row for a client-side add. Negative id marks it "new"; the five
 *  rendering types default to CUSTOM so the editor opens on the template
 *  accordions; everything else is null (the backend applies model defaults —
 *  buildPatch only sends non-null fields for new rows). */
function newDeliveryMethod(method: number, sequence: number): NotificationDeliveryMethod {
  return {
    id: -Date.now(),
    method,
    sequence,
    enabled: false,
    theme: null,
    email_subject: null,
    email_message: null,
    email_message_text: null,
    email_rendering_type: RENDER_CUSTOM,
    email_template: null,
    sms_message: null,
    sms_rendering_type: RENDER_CUSTOM,
    sms_template: null,
    whatsapp_message: null,
    whatsapp_rendering_type: RENDER_CUSTOM,
    whatsapp_template: null,
    chat_message: null,
    chat_rendering_type: RENDER_CUSTOM,
    chat_template: null,
    airbnb_message: null,
    airbnb_rendering_type: RENDER_CUSTOM,
    airbnb_template: null,
    sns_subject: null,
    sns_message: null,
    sentry_message: null,
  };
}

function refsToWrite(refs: BlockRefItem[]): BlockRefWrite[] {
  return refs.map((r) => ({
    // Negative ids mark rows added client-side — the funnel creates them.
    id: r.id > 0 ? r.id : null,
    block: r.block,
    block_list: r.block_list,
    alias: r.alias,
  }));
}

export function useNotificationDraft(detail: NotificationDetail | undefined): NotificationDraft {
  const [state, setState] = useState<DraftState | null>(null);
  const [dirtyPaths, setDirtyPaths] = useState<Set<string>>(new Set());
  // The server baseline the dirty flags are relative to.
  const baselineRef = useRef<DraftState | null>(null);

  const ensure = (): DraftState => {
    if (state) return state;
    if (!detail) throw new Error('draft accessed before load');
    const init = initialState(detail);
    baselineRef.current = initialState(detail);
    return init;
  };

  // Lazy-init when the detail arrives (guarded render-phase state adjustment —
  // the sanctioned React pattern for deriving state from async props once).
  if (state === null && detail) {
    const init = initialState(detail);
    baselineRef.current = initialState(detail);
    setState(init);
  }

  const markDirty = useCallback((paths: string[]) => {
    setDirtyPaths((prev) => {
      const next = new Set(prev);
      for (const p of paths) next.add(p);
      return next;
    });
  }, []);

  const setMeta = useCallback<NotificationDraft['setMeta']>(
    (key, value) => {
      setState((s) => (s ? { ...s, meta: { ...s.meta, [key]: value } } : s));
      markDirty([`notification.${String(key)}`]);
    },
    [markDirty],
  );

  const setDmField = useCallback<NotificationDraft['setDmField']>(
    (dmId, field, value) => {
      setState((s) =>
        s
          ? {
              ...s,
              deliveryMethods: s.deliveryMethods.map((dm) =>
                dm.id === dmId ? { ...dm, [field]: value } : dm,
              ),
            }
          : s,
      );
      markDirty([`delivery_methods[${dmId}].${field}`]);
    },
    [markDirty],
  );

  const setDeliveryMethods = useCallback<NotificationDraft['setDeliveryMethods']>(
    (dms, path = 'delivery_methods') => {
      setState((s) => (s ? { ...s, deliveryMethods: dms } : s));
      markDirty([path]);
    },
    [markDirty],
  );

  const addDeliveryMethod = useCallback<NotificationDraft['addDeliveryMethod']>(
    (method) => {
      const row = newDeliveryMethod(method, 0);
      setState((s) => {
        if (!s) return s;
        const dm = { ...row, sequence: s.deliveryMethods.length };
        return { ...s, deliveryMethods: [...s.deliveryMethods, dm] };
      });
      markDirty(['delivery_methods']);
      return row.id;
    },
    [markDirty],
  );

  const removeDeliveryMethod = useCallback<NotificationDraft['removeDeliveryMethod']>(
    (dmId) => {
      setState((s) =>
        s ? { ...s, deliveryMethods: s.deliveryMethods.filter((dm) => dm.id !== dmId) } : s,
      );
      markDirty(['delivery_methods']);
    },
    [markDirty],
  );

  const setBlockRefs = useCallback<NotificationDraft['setBlockRefs']>(
    (refs) => {
      setState((s) => (s ? { ...s, blockRefs: refs } : s));
      markDirty(['block_refs']);
    },
    [markDirty],
  );

  const setRules = useCallback<NotificationDraft['setRules']>(
    (rules) => {
      setState((s) => (s ? { ...s, rules } : s));
      markDirty(['rules']);
    },
    [markDirty],
  );

  const setAdvancedRules = useCallback<NotificationDraft['setAdvancedRules']>(
    (rows) => {
      setState((s) => (s ? { ...s, advancedRules: rows } : s));
      markDirty(['advanced_rules']);
    },
    [markDirty],
  );

  const setAutoReplies = useCallback<NotificationDraft['setAutoReplies']>(
    (value) => {
      setState((s) => (s ? { ...s, autoReplies: value } : s));
      markDirty(['auto_replies']);
    },
    [markDirty],
  );

  const applySnapshot = useCallback<NotificationDraft['applySnapshot']>(
    (snapshot) => {
      const baseline = baselineRef.current;
      const paths: string[] = [];
      const snapMeta = (snapshot.notification ?? {}) as Partial<NotificationCore>;
      const snapDms = (snapshot.delivery_methods ?? []) as NotificationDeliveryMethod[];
      const snapRefs = (snapshot.block_refs ?? []) as unknown as BlockRefItem[];
      // Snapshot rule rows are the exact read shape (nested_ruleset = bare id).
      // Rule ids that no longer exist on this notification must become CREATE
      // rows (negative client id → `id: null` on save) — the write path 400s
      // on foreign/dead ids.
      const liveRuleIds = new Set((baseline?.rules ?? []).map((r) => r.id));
      const snapRules = draftsFromDetail(
        ((snapshot as unknown as { rules?: RuleWire[] }).rules ?? []) as RuleWire[],
      ).map((r) => (liveRuleIds.has(r.id) ? r : { ...r, id: allocClientRuleId() }));

      if (baseline) {
        for (const key of Object.keys(snapMeta)) {
          if (
            JSON.stringify(snapMeta[key as keyof NotificationCore]) !==
            JSON.stringify(baseline.meta[key as keyof NotificationCore])
          ) {
            paths.push(`notification.${key}`);
          }
        }
        const baseById = new Map(baseline.deliveryMethods.map((dm) => [dm.id, dm]));
        for (const dm of snapDms) {
          const base = baseById.get(dm.id);
          if (!base) {
            paths.push(`delivery_methods[${dm.id}]`);
            continue;
          }
          for (const key of Object.keys(dm)) {
            if (JSON.stringify(dm[key]) !== JSON.stringify(base[key])) {
              paths.push(`delivery_methods[${dm.id}].${key}`);
            }
          }
        }
        if (JSON.stringify(snapRefs) !== JSON.stringify(baseline.blockRefs)) {
          paths.push('block_refs');
        }
        if (JSON.stringify(rulesToWrite(snapRules)) !== JSON.stringify(rulesToWrite(baseline.rules))) {
          paths.push('rules');
        }
      } else {
        paths.push('notification', 'delivery_methods', 'block_refs', 'rules');
      }

      // Advanced rules: only restorable from snapshots taken after the M2M
      // lists were added to the aggregate — older rows would fail validation.
      const snapAdvRaw = (snapshot as unknown as { advanced_rules?: AdvancedRuleRow[] }).advanced_rules;
      const snapAdv =
        Array.isArray(snapAdvRaw) &&
        snapAdvRaw.every((r) => Array.isArray(r.trigger_notification_delivery_methods))
          ? snapAdvRaw
          : null;
      if (
        snapAdv &&
        baseline &&
        JSON.stringify(advancedRulesToWrite(snapAdv)) !==
          JSON.stringify(advancedRulesToWrite(baseline.advancedRules))
      ) {
        paths.push('advanced_rules');
      }

      // Auto replies: key absent in old snapshots → leave untouched.
      const snapAutoRaw = (snapshot as unknown as { auto_replies?: AutoReplies | null }).auto_replies;
      const hasSnapAuto = snapAutoRaw !== undefined && snapAutoRaw !== null;
      const snapAuto = hasSnapAuto ? normalizeAutoReplies(snapAutoRaw) : null;
      if (
        snapAuto &&
        baseline &&
        JSON.stringify(autoRepliesToWrite(snapAuto)) !==
          JSON.stringify(baseline.autoReplies ? autoRepliesToWrite(baseline.autoReplies) : null)
      ) {
        paths.push('auto_replies');
      }
      // Option ids that no longer exist on this notification must become
      // creates — the write path 400s on foreign/dead ids.
      const liveOptionIds = new Set(
        (baseline?.autoReplies?.options ?? []).map((o) => o.id).filter((id) => id != null),
      );
      const restoredAuto = snapAuto
        ? {
            ...snapAuto,
            options: snapAuto.options.map((o) =>
              o.id != null && !liveOptionIds.has(o.id) ? { ...o, id: null } : o,
            ),
          }
        : null;

      setState((s) =>
        s
          ? {
              meta: { ...s.meta, ...snapMeta },
              deliveryMethods: snapDms.map((dm) => ({ ...dm })),
              blockRefs: snapRefs.map((r) => ({ ...r })),
              rules: snapRules,
              advancedRules: snapAdv ?? s.advancedRules,
              autoReplies: restoredAuto ?? s.autoReplies,
            }
          : s,
      );
      markDirty(paths.length ? paths : ['notification']);
    },
    [markDirty],
  );

  const buildPatch = useCallback<NotificationDraft['buildPatch']>(
    (extra) => {
      const s = ensure();
      const body: NotificationWrite = { ...extra };

      const metaDirty = [...dirtyPaths].filter((p) => p.startsWith('notification.'));
      if (metaDirty.length > 0) {
        const notification: Partial<NotificationCore> = {};
        for (const path of metaDirty) {
          const key = path.slice('notification.'.length) as keyof NotificationCore;
          (notification as Record<string, unknown>)[key as string] = s.meta[key];
        }
        body.notification = notification;
      }

      const dmDirty = [...dirtyPaths].some((p) => p.startsWith('delivery_methods'));
      if (dmDirty) {
        // COMPLETE ordered list (missing rows would be deleted by the
        // reconcile — never omit one). Rows added client-side carry a negative
        // id: serialize them as `{id: null, ...}` CREATE rows with every
        // non-null field they hold (the funnel needs at least method+enabled;
        // never send the negative id). Existing rows stay sparse (id + dirty
        // fields), plus `sequence` whenever the list order itself changed —
        // the local list is already resequenced by position.
        const orderDirty = dirtyPaths.has('delivery_methods');
        body.delivery_methods = s.deliveryMethods.map((dm) => {
          if (dm.id < 0) {
            const row: Record<string, unknown> = { id: null };
            for (const [field, value] of Object.entries(dm)) {
              if (field === 'id' || value === null || value === undefined) continue;
              row[field] = value;
            }
            return row as DeliveryMethodWrite;
          }
          const row: Record<string, unknown> = { id: dm.id };
          if (orderDirty) row.sequence = dm.sequence;
          const prefix = `delivery_methods[${dm.id}].`;
          for (const path of dirtyPaths) {
            if (path.startsWith(prefix)) {
              const field = path.slice(prefix.length);
              row[field] = dm[field];
            }
          }
          return row as DeliveryMethodWrite;
        });
      }

      if (dirtyPaths.has('block_refs')) {
        body.block_refs = refsToWrite(s.blockRefs);
      }
      if (dirtyPaths.has('rules')) {
        // COMPLETE list — the reconcile deletes omitted rows, so untouched
        // rows (incl. expression rules and deprecated templates) are carried
        // verbatim.
        body.rules = rulesToWrite(s.rules);
      }
      if (dirtyPaths.has('advanced_rules')) {
        body.delivery_method_advanced_rules = advancedRulesToWrite(s.advancedRules);
      }
      if (dirtyPaths.has('auto_replies') && s.autoReplies) {
        body.auto_replies = autoRepliesToWrite(s.autoReplies);
      }
      return body;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dirtyPaths, state, detail],
  );

  const reset = useCallback((freshDetail: NotificationDetail) => {
    const init = initialState(freshDetail);
    baselineRef.current = initialState(freshDetail);
    setState(init);
    setDirtyPaths(new Set());
  }, []);

  const draft = useMemo<DraftState>(
    () =>
      state ?? {
        meta: {},
        deliveryMethods: [],
        blockRefs: [],
        rules: [],
        advancedRules: [],
        autoReplies: null,
      },
    [state],
  );

  return {
    draft,
    dirtyPaths,
    dirty: dirtyPaths.size > 0,
    setMeta,
    setDmField,
    setDeliveryMethods,
    addDeliveryMethod,
    removeDeliveryMethod,
    setBlockRefs,
    setRules,
    setAdvancedRules,
    setAutoReplies,
    applySnapshot,
    buildPatch,
    reset,
  };
}

export { META_KEYS };
